import React, { useState } from 'react';
import { auth, database, storage } from '../firebase';
import { ref, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Eye, EyeOff, X, Edit2 } from 'lucide-react';

const TimelinePost = ({ post }) => {
  const isAuthed = auth.currentUser;
  const [showImage, setShowImage] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editForm, setEditForm] = useState({
    title: post.title || '',
    description: post.description || '',
    imageUrl: post.imageUrl || ''
  });
  const [newImageFile, setNewImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta publicación?')) {
      try {
        await remove(ref(database, `timeline/${post.id}`));
      } catch (error) {
        console.error('Error deleting post: ', error);
      }
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let imageUrl = editForm.imageUrl;

      // Si hay una nueva imagen, subirla a Firebase Storage
      if (newImageFile) {
        // Si había una imagen anterior, eliminarla
        if (post.imageUrl) {
          try {
            const oldImageRef = storageRef(storage, `timeline/${post.id}`);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log('No se pudo eliminar la imagen anterior:', error);
          }
        }

        // Subir la nueva imagen
        const imageRef = storageRef(storage, `timeline/${post.id}_${Date.now()}`);
        await uploadBytes(imageRef, newImageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Actualizar el post en Firebase
      const postRef = ref(database, `timeline/${post.id}`);
      await update(postRef, {
        title: editForm.title,
        description: editForm.description,
        imageUrl: imageUrl,
        updatedAt: Date.now()
      });

      setShowEditModal(false);
      setNewImageFile(null);
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Error al actualizar la publicación');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setNewImageFile(e.target.files[0]);
      // Crear una URL temporal para previsualizar la imagen
      const reader = new FileReader();
      reader.onload = (event) => {
        setEditForm({ ...editForm, imageUrl: event.target.result });
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const openEditModal = () => {
    setEditForm({
      title: post.title || '',
      description: post.description || '',
      imageUrl: post.imageUrl || ''
    });
    setNewImageFile(null);
    setShowEditModal(true);
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl border-2 border-transparent hover:border-blue-200 flex flex-col h-full">
        {post.imageUrl && showImage && (
          <div className="relative h-56 overflow-hidden flex-shrink-0">
            <img
              src={post.imageUrl}
              alt={post.title || 'Timeline Image'}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105 cursor-pointer"
              onClick={() => setShowModal(true)}
            />
            {post.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent text-white p-4">
                <h2 className="text-xl font-bold drop-shadow-lg line-clamp-2">{post.title}</h2>
              </div>
            )}
          </div>
        )}
      <div className="p-6 flex-1 flex flex-col">
        {post.title && (!post.imageUrl || !showImage) && (
          <h2 className="text-2xl font-bold mb-3 text-gray-800 bg-gradient-to-r from-blue-700 to-amber-500 bg-clip-text text-transparent">
            {post.title}
          </h2>
        )}
        {post.description && (
          <div className="mb-4 flex-1">
            <p
              className={`text-gray-700 leading-relaxed text-base whitespace-pre-wrap cursor-pointer ${!isExpanded ? 'line-clamp-4' : ''}`}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {post.description}
            </p>
            {post.description.length > 150 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-blue-500 hover:text-blue-700 text-sm font-medium mt-1"
              >
                {isExpanded ? 'Ver menos' : 'Ver más'}
              </button>
            )}
          </div>
        )}

        <div className="mt-auto space-y-3">
          {/* Botón para ocultar/mostrar imagen - Solo para usuarios logeados */}
          {post.imageUrl && isAuthed && (
            <button
              onClick={() => setShowImage(!showImage)}
              className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              {showImage ? (
                <>
                  <EyeOff size={18} />
                  <span>Ocultar imagen</span>
                </>
              ) : (
                <>
                  <Eye size={18} />
                  <span>Mostrar imagen</span>
                </>
              )}
            </button>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs">{new Date(post.createdAt).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}</span>
            </div>
            {isAuthed && (
              <div className="flex gap-2">
                <button
                  onClick={openEditModal}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1.5 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1.5 text-sm"
                >
                  <Edit2 size={16} />
                  Editar
                </button>
                <button
                  onClick={handleDelete}
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold py-1.5 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1.5 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Eliminar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Modal para ver imagen en tamaño completo */}
    {showModal && (
      <div
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
        onClick={() => setShowModal(false)}
      >
        <button
          onClick={() => setShowModal(false)}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          aria-label="Cerrar"
        >
          <X size={32} />
        </button>
        <img
          src={post.imageUrl}
          alt={post.title || 'Timeline Image'}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}

    {/* Modal de edición */}
    {showEditModal && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowEditModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-2xl">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-amber-500 bg-clip-text text-transparent">
              Editar Publicación
            </h2>
            <button
              onClick={() => setShowEditModal(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Cerrar"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleEdit} className="p-6 space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título
              </label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Título de la publicación"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Descripción de la publicación"
              />
            </div>

            {/* Imagen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagen
              </label>
              {editForm.imageUrl && (
                <div className="mb-4">
                  <img
                    src={editForm.imageUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-2 text-sm text-gray-500">
                Selecciona una nueva imagen para reemplazar la actual
              </p>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isUploading}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                {isUploading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </>
  );
};

export default TimelinePost;
